import { Update, Upload, Visibility } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { XMLParser } from 'fast-xml-parser';
import {
  expressCheckDocumentCessionsStatus,
  expressCheckDocumentsWithXML,
} from 'lib/api/express/factoring';
import {
  expressDownloadFilesFromRequest,
  expressUploadFilesFromRequest,
} from 'lib/api/express/files';
import DocumentsPreviewer, {
  useDocumentPreview,
} from 'lib/common/DocumentsPreviewer';
import Uploader from 'lib/designSystem/Uploader';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  executeCession,
  getCompanyDetails,
  getSelectedCertificate,
} from '../../../../../../../../../../../../../lib/api/express';
import CustomButton from '../../../../../../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../../../../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../../../../../../../../../lib/designSystem/Table';
import {
  showAlert,
  validatePermissions,
} from '../../../../../../../../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../../../../../../../../services/redux/authSlice';
import CessionConfirmationDialog from './components/confirmation';
import CessionStatusDialog from './components/status';

const INVALID_CHAR_PATTERN =
  '[^\\u0009\\u000A\\u000D\\u0020-\\u007F\\u00A0-\\u00FF]';
const INVALID_CHAR_RE = new RegExp(INVALID_CHAR_PATTERN, 'g');

const XML_DECL_PATTERN = '<\\?xml(.*?)encoding="[^"]*"(.*?)\\?>';
const XML_DECL_RE = new RegExp(XML_DECL_PATTERN);

// Sanitizador reutilizable
function sanitizeXmlString(text: string): string {
  // Reemplazo específico opcional (ej.: el � típico de Nº)
  // Si no quieres asumir "Ñ", puedes dejarlo como vacío o reemplazar por 'º' si corresponde a "Nº".
  text = text.replace(/\uFFFD/g, ''); // o '.replace(/\uFFFD/g, "º")' según el caso

  // Eliminar caracteres no permitidos por XML 1.0 (permitimos TAB, LF, CR y Latin-1)
  text = text.replace(INVALID_CHAR_RE, '');

  // Normalizar a UTF-8 en la declaración XML (si existe)
  text = text.replace(XML_DECL_RE, '<?xml$1encoding="UTF-8"$2?>');

  return text;
}

const DocumentCessionModule = (props: any) => {
  const { selectedRequest, setOpenShoppingCart } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [openConfirmationModule, setOpenConfirmationModule] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<any>(null);
  const [passphrase, setPassphrase] = useState<string>('');
  const [companyDetails, setCompanyDetails] = useState<any>({});
  const [documentToUpload, setDocumentToUpload] = useState('');
  const [openUploadDocumentPDF, setOpenUploadDocumentPDF] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [documents, setDocuments] = useState<any[]>([]);
  const [
    openCheckDocumentsCessionsStatus,
    setOpenCheckDocumentsCessionsStatus,
  ] = useState(false);
  const [documentStatusResponse, setDocumentStatusResponse] = useState<{
    folio: string;
    status: 'accepted' | 'rejected' | 'pending' | 'invalid';
    message: string;
    subType: string;
    businessIdentifier: string;
  } | null>(null);
  const [file, setFile] = useState<any>(null);
  const {
    previewOpen,
    previewLoading,
    previewError,
    currentPreviewFile,
    blobUrl,
    contentType,
    handlePreview,
    handleClosePreview,
    setPreviewError,
    canPreviewFileName,
  } = useDocumentPreview({
    requestId: selectedRequest?.id || '',
    requestType: 'document',
  });
  const clientId = user?.clientId;

  const openCessionModalConfirm = () => {
    setOpenConfirmationModule(true);
  };

  const handleCession = async () => {
    setLoading(true);
    try {
      const documents = selectedRequest.documents.filter((doc: any) =>
        selected.includes(doc.id),
      );
      const cessionResponse = await executeCession(
        dispatch,
        clientId as string,
        {
          certificateId: selectedCertificate.id,
          passphrase,
          documents,
        },
      );
      const errors = cessionResponse.results.filter(
        (result: any) => !result.status,
      );
      if (errors.length > 0) {
        showAlert(
          dispatch,
          'error',
          `Error al ceder documentos. ${errors.map((error: any) => error.id).join(', ')}`,
        );
      } else {
        showAlert(
          dispatch,
          'success',
          'Documentos cedidos correctamente. En unos minutos se reflejarán los cambios.',
        );
        setOpenConfirmationModule(false);
        setOpenShoppingCart(false);
      }
      setOpenConfirmationModule(false);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al ceder documentos.');
    }
    setLoading(false);
  };

  const handleUploadDocumentPDF = (documentId: string) => {
    setOpenUploadDocumentPDF(true);
    const document = documents.find((x: any) => x.id === documentId);
    const documentKey = `${document.businessIdentifier}-${document.folio}-${document.subType}`;
    setDocumentToUpload(documentKey);
  };

  const handleDownloadDocumentPDF = async (documentId: string) => {
    const document = documents.find((x: any) => x.id === documentId);
    const documentKey = `${document.businessIdentifier}-${document.folio}-${document.subType}`;
    setLoading(true);
    try {
      const data = {
        requestId: documentKey,
        fileName: `${documentKey}.xml`,
        requestType: 'document',
      };
      const response = await expressDownloadFilesFromRequest(
        dispatch,
        data,
        clientId as string,
      );
      if (response) {
        window.open(response);
      } else {
        showAlert(dispatch, 'error', 'No hay archivo para descargar');
      }
    } catch (error) {}
    setLoading(false);
  };

  const handlePreviewDocumentPDF = async (documentId: string) => {
    const document = documents.find((x: any) => x.id === documentId);
    const documentKey = `${document.businessIdentifier}-${document.folio}-${document.subType}`;
    const fileName = `${documentKey}.xml`;

    if (!canPreviewFileName(fileName)) {
      showAlert(
        dispatch,
        'error',
        'Este tipo de archivo no se puede previsualizar',
      );
      return;
    }

    try {
      await handlePreview(fileName);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al previsualizar el documento');
    }
  };

  const handleFileChange = async (event: any) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;
    setUploadedFileName(uploadedFile.name);
    try {
      // 1️⃣ Leer el archivo como texto (asumiendo ISO-8859-1 por defecto)
      const buffer = await uploadedFile.arrayBuffer();
      let text = new TextDecoder('latin1').decode(buffer);
      text = sanitizeXmlString(text);
      // 4️⃣ Validar estructura XML
      const parser = new XMLParser({ ignoreAttributes: false });
      parser.parse(text); // lanza error si está mal formado
      // 5️⃣ Si pasa, guardar el XML limpio
      const cleanedFile = new File([text], uploadedFile.name, {
        type: 'text/xml',
      });
      setFile(cleanedFile);
    } catch (error: any) {
      alert('El archivo XML contiene errores o caracteres inválidos.');
      setFile(null);
    }
  };

  const uploadFiles = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      // Change the file Name to name prop
      const fileToUpload = new File([file], `${documentToUpload}.xml`, {
        type: file.type,
      });
      formData.append('files', fileToUpload);
      await expressUploadFilesFromRequest(
        dispatch,
        formData,
        {
          requestId: documentToUpload,
          requestType: 'document',
        },
        clientId as string,
      );
      setDocumentToUpload('');
      setOpenUploadDocumentPDF(false);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const handleCheckDocumentsCessionsStatus = async (documentId: string) => {
    try {
      const document = documents.find((x: any) => x.id === documentId);
      const documentKey = `${document.businessIdentifier}-${document.folio}-${document.subType}`;
      const response = await expressCheckDocumentCessionsStatus(
        dispatch,
        [documentId],
        clientId as string,
      );
      const documentStatus = response[documentKey];
      setDocumentStatusResponse({
        folio: document.folio,
        subType: document.subType,
        status: documentStatus.status,
        message: documentStatus.message,
        businessIdentifier: document.businessIdentifier,
      });
      setOpenCheckDocumentsCessionsStatus(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCheckDocumentsCessionsStatusObject = {
    key: 'checkDocumentsCessionsStatus',
    label: '',
    handleAction: handleCheckDocumentsCessionsStatus,
    icon: <Update />,
  };

  const handleUploadDocumentPDFObject = {
    key: 'uploadDocumentPDF',
    label: '',
    handleAction: handleUploadDocumentPDF,
    icon: <Upload />,
  };

  const handlePreviewDocumentPDFObject = {
    key: 'previewDocumentPDF',
    label: '',
    handleAction: handlePreviewDocumentPDF,
    icon: <Visibility />,
    keyDisabled: 'canBeCeded',
  };

  const documentsHeaders = [
    handleCheckDocumentsCessionsStatusObject,
    handleUploadDocumentPDFObject,
    handlePreviewDocumentPDFObject,
    // handlePreviewDocumentPDFObject,
    { key: 'folio', label: 'Folio' },
    {
      key: 'stakeholderName',
      label: 'Pagador',
    },
    {
      key: 'cedded',
      label: 'Cedida',
      type: 'cession-lights',
      canNull: true,
    },
    {
      key: 'confirmDate',
      label: 'Confirmada',
      type: 'confirm-lights',
      canNull: true,
    },
    {
      key: 'cancelDate',
      label: 'Reclamos',
      type: 'cancel-lights',
      canNull: true,
    },
    {
      key: 'amountDocument',
      label: 'Monto',
      type: 'money',
    },
  ];

  const canSelect = () => {
    if (!selectedCertificate) return false;
    if (!validatePermissions(user, 'factoring_cession_create')) return false;
    return true;
  };

  const buttons: CustomButtonProps[] = [
    {
      name: 'Ceder',
      action: openCessionModalConfirm,
      color: 'primary',
      disabled:
        !validatePermissions(user, 'factoring_cession_create') ||
        !selected.length ||
        !selectedCertificate,
    },
  ];

  const setUp = async () => {
    setLoading(true);
    await setUpCompanyDetails();
    await checkDocumentsWithXML();
    setLoading(false);
  };

  const checkDocumentsWithXML = async () => {
    try {
      const documentsIds = selectedRequest.documents.map((doc: any) => doc.id);
      const response = await expressCheckDocumentsWithXML(
        dispatch,
        documentsIds,
        clientId as string,
      );
      for (const document of selectedRequest.documents) {
        const documentKey = `${document.businessIdentifier}-${document.folio}-${document.subType}`;
        document.canBeCeded = response[documentKey];
      }
      setDocuments(selectedRequest.documents);
    } catch (error) {
      console.log(error);
    }
  };

  const cantSelectRow = (document: any) => {
    if (!document.canBeCeded) return true;
    if (document.cedded) return true;
  };

  const setUpCompanyDetails = async () => {
    try {
      const response = await getCompanyDetails(dispatch, clientId as string);
      const selectedCertificateFromResponse = await getSelectedCertificate(
        dispatch,
        clientId as string,
      );
      setSelectedCertificate(selectedCertificateFromResponse);
      setCompanyDetails(response);
    } catch (error) {
      console.log(selectedCertificate);
      console.log(error);
      alert('No se pudieron obtener los detalles de la empresa');
    }
  };

  useEffect(() => {
    setUp();
  }, []);

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      <HeaderWithActions variant="body1" buttons={buttons} />
      <EnhancedTable
        dataHeaders={documentsHeaders}
        data={documents}
        rowsPerPageDefault={5}
        finalSumRow
        selected={canSelect() ? selected : null}
        setSelected={setSelected}
        cantSelectRow={cantSelectRow}
      />
      {!selectedCertificate && (
        <Typography margin="dense" variant="caption" color="error">
          Para poder ceder facturas, debes tener un certificado seleccionado el
          cual tenga acceso a la empresa.
        </Typography>
      )}
      <DialogActions>
        <CustomButton
          onClick={() => setOpenShoppingCart(false)}
          color="secondary"
        >
          Salir
        </CustomButton>
      </DialogActions>
      {openConfirmationModule && (
        <CessionConfirmationDialog
          open={openConfirmationModule}
          setOpen={setOpenConfirmationModule}
          handleCession={handleCession}
          passphrase={passphrase}
          setPassphrase={setPassphrase}
          selectedCertificate={selectedCertificate}
          companyDetails={companyDetails}
          selected={selected}
          loading={loading}
        />
      )}

      {openUploadDocumentPDF && (
        <Dialog
          open={openUploadDocumentPDF}
          onClose={() => setOpenUploadDocumentPDF(false)}
        >
          <DialogTitle id="form-dialog-title">Subir factura</DialogTitle>
          <DialogContent style={{ minHeight: 100, padding: 50 }}>
            <Uploader
              buttonText="Subir documento"
              handleFileChange={handleFileChange}
              uploadedFileName={uploadedFileName}
              setUploadedFileName={setUploadedFileName}
              accept="application/xml"
            />
          </DialogContent>
          <DialogActions>
            <CustomButton
              onClick={() => setOpenUploadDocumentPDF(false)}
              color="secondary"
            >
              Cancelar
            </CustomButton>
            <CustomButton onClick={uploadFiles} color="primary">
              Subir
            </CustomButton>
          </DialogActions>
        </Dialog>
      )}
      <CessionStatusDialog
        open={openCheckDocumentsCessionsStatus}
        onClose={() => setOpenCheckDocumentsCessionsStatus(false)}
        documentStatus={documentStatusResponse}
      />
      <DocumentsPreviewer
        open={previewOpen}
        onClose={handleClosePreview}
        fileName={currentPreviewFile ?? ''}
        blobUrl={blobUrl}
        contentType={contentType}
        loading={previewLoading}
        error={previewError}
        onDownload={() => {
          if (currentPreviewFile) {
            const document = documents.find(
              (x: any) =>
                `${x.businessIdentifier}-${x.folio}-${x.subType}.xml` ===
                currentPreviewFile,
            );
            if (document) {
              handleDownloadDocumentPDF(document.id);
            }
          }
        }}
        onError={(error) => setPreviewError(error)}
      />
    </div>
  );
};

export default DocumentCessionModule;
